import { useClerk, useUser } from '@clerk/react'
import { LayoutDashboard, LogOut, Settings2 } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'

/**
 * Custom account menu — replaces Clerk's <UserButton> so the avatar opens our own
 * styled dropdown instead of Clerk's popover. "Manage account" still opens Clerk's
 * (themed) profile because account/security management should use its vetted flows.
 */
export function UserMenu() {
  const { user } = useUser()
  const { signOut, openUserProfile } = useClerk()
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const onPointerDown = (event: PointerEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) setOpen(false)
    }
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false)
    }
    document.addEventListener('pointerdown', onPointerDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  if (!user) return null

  const name = user.fullName || user.username || 'Eclipse user'
  const email = user.primaryEmailAddress?.emailAddress ?? (user.username ? `@${user.username}` : '')
  const initial = name.slice(0, 1).toUpperCase()

  const go = (action: () => void) => {
    setOpen(false)
    action()
  }

  return (
    <div className="user-menu" ref={rootRef}>
      <button
        className="user-menu__avatar"
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Account menu"
      >
        {user.imageUrl ? <img src={user.imageUrl} alt="" /> : <span>{initial}</span>}
      </button>

      {open ? (
        <div className="user-menu__panel t-panel-reveal" role="menu">
          <div className="user-menu__head">
            <span className="user-menu__pic">{user.imageUrl ? <img src={user.imageUrl} alt="" /> : initial}</span>
            <div><strong>{name}</strong>{email ? <small>{email}</small> : null}</div>
          </div>
          <div className="user-menu__items">
            <button type="button" role="menuitem" onClick={() => go(() => navigate('/account'))}>
              <LayoutDashboard size={15} /> My account
            </button>
            <button type="button" role="menuitem" onClick={() => go(() => openUserProfile())}>
              <Settings2 size={15} /> Manage account
            </button>
            <button type="button" role="menuitem" className="user-menu__danger" onClick={() => go(() => void signOut({ redirectUrl: '/' }))}>
              <LogOut size={15} /> Sign out
            </button>
          </div>
        </div>
      ) : null}
    </div>
  )
}
